import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Receipt,
  RefreshCw,
  Search,
  Calendar,
  Clock,
  User,
  Phone,
  Mail,
  Eye,
  Printer,
  ChevronLeft,
  ChevronRight,
  ShoppingBag,
  CreditCard,
  FileText,
  Hash,
  DollarSign,
  Package,
  Filter,
  X,
  Globe,
  MonitorSmartphone,
  UserCog,
  ShoppingCart,
  Store,
  Tablet,
  ArrowRightLeft,
  History,
  Pencil,
  Shield,
  Ban,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

// API endpoints
const BACKEND_API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_API_BASE}/api`;

// WebSocket URL helper
const getWebSocketUrl = () => {
  const noTrailing = BACKEND_API_BASE.replace(/\/+$/g, '').replace(/\/api$/i, '');
  const wsBase = noTrailing.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws`;
};

// Kiosk API endpoints
const KIOSK_API_BASE = process.env.REACT_APP_KIOSK_API_BASE || 'https://pickleball-kiosk.plgucamsur.com/api';
const KIOSK_TOKEN_URL = `${KIOSK_API_BASE}/kiosk/token`;
const KIOSK_TRANSACTIONS_URL = `${KIOSK_API_BASE}/transactions`;

// Kiosk token cache (shared across component renders)
let kioskTokenCache = { token: null, expiresAt: null };
let kioskTokenPromise = null;

// Format time to 12-hour (e.g. "13:00" → "1:00 pm", "08:30" → "8:30 am")
const formatTime12 = (timeStr) => {
  if (!timeStr) return '-';
  const raw = String(timeStr).trim();

  const match24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (match24) {
    let hour = parseInt(match24[1], 10);
    const min = match24[2];
    const ampm = hour >= 12 ? 'pm' : 'am';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${min} ${ampm}`;
  }

  const matchAmPm = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (matchAmPm) {
    const hour = parseInt(matchAmPm[1], 10);
    const min = matchAmPm[2] || '00';
    const ampm = matchAmPm[3].toLowerCase();
    return `${hour}:${min} ${ampm}`;
  }

  return raw;
};

// Format date nicely
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Format datetime with 12-hour time
const formatDateTime12 = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const datePart = date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${datePart}, ${hours}:${minutes} ${ampm}`;
};

// Format currency
const formatCurrency = (amount) => {
  const num = Number(amount) || 0;
  return `₱${num.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Determine booking source label and styling
const getBookingSourceInfo = (booking) => {
  const source = (booking.source || '').toLowerCase();
  const bookingType = (booking.booking_type || '').toLowerCase();
  const transactionType = (booking.transaction_type || '').toLowerCase();
  const hasCreatedByAdmin = Boolean(booking.created_by_admin || booking.created_by);

  if (bookingType === 'walk_in' || transactionType === 'walk_in') {
    return {
      label: 'Walk-in',
      icon: MonitorSmartphone,
      color: 'bg-blue-100 text-blue-700 border-blue-300'
    };
  }
  if (source === 'admin_reservation' || transactionType === 'admin_reservation' || (hasCreatedByAdmin && source !== 'web')) {
    return {
      label: 'Admin Reserve',
      icon: UserCog,
      color: 'bg-purple-100 text-purple-700 border-purple-300'
    };
  }
  // POS Purchase
  if (booking._type === 'pos_order') {
    return {
      label: 'POS Purchase',
      icon: Store,
      color: 'bg-orange-100 text-orange-700 border-orange-300'
    };
  }
  // Kiosk Booking
  if (booking._type === 'kiosk_transaction') {
    return {
      label: 'Kiosk',
      icon: Tablet,
      color: 'bg-violet-100 text-violet-700 border-violet-300'
    };
  }
  // Default: website online payment
  return {
    label: 'Online Payment',
    icon: Globe,
    color: 'bg-sky-100 text-sky-700 border-sky-300'
  };
};

const AdminWebLog = () => {
  const { isDarkMode } = useAdminTheme();
  const { token } = useAdminAuth();

  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // Receipt modal
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);

  // Add-ons master data (for price lookups on older bookings)
  const [addOnsMaster, setAddOnsMaster] = useState([]);

  // Get kiosk token with caching and dedup
  const getKioskToken = async () => {
    const now = Date.now();
    if (kioskTokenCache.token && kioskTokenCache.expiresAt && kioskTokenCache.expiresAt > now + 60000) {
      return kioskTokenCache.token;
    }
    if (kioskTokenPromise) return await kioskTokenPromise;
    kioskTokenPromise = (async () => {
      try {
        const res = await fetch(KIOSK_TOKEN_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ pos_id: 'cwc' }),
          mode: 'cors',
        });
        if (!res.ok) return null;
        const data = await res.json();
        const kioskToken = data?.token;
        const expiresIn = data?.expires_in || 86400;
        if (!kioskToken) return null;
        kioskTokenCache = { token: kioskToken, expiresAt: now + (expiresIn * 1000) - 60000 };
        return kioskToken;
      } catch { return null; } finally { kioskTokenPromise = null; }
    })();
    return await kioskTokenPromise;
  };

  // Fetch kiosk transactions
  const fetchKioskTransactions = async () => {
    try {
      const kioskToken = await getKioskToken();
      if (!kioskToken) return [];
      const res = await fetch(KIOSK_TRANSACTIONS_URL, { headers: { 'X-KIOSK-TOKEN': kioskToken } });
      if (!res.ok) return [];
      const responseData = await res.json();
      return Array.isArray(responseData?.data) ? responseData.data : [];
    } catch { return []; }
  };

  // Format kiosk time for display
  const formatKioskTime = (timeStr) => {
    if (!timeStr) return '';
    const raw = String(timeStr).trim();
    const m24 = raw.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (m24) {
      let h = parseInt(m24[1], 10);
      const min = m24[2];
      const ap = h >= 12 ? 'pm' : 'am';
      h = h % 12; if (h === 0) h = 12;
      return `${h}:${min} ${ap}`;
    }
    const mAP = raw.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)$/i);
    if (mAP) {
      return `${parseInt(mAP[1], 10)}:${mAP[2] || '00'} ${mAP[3].toLowerCase()}`;
    }
    return raw;
  };

  // Normalize kiosk transactions to booking-like objects
  const normalizeKioskTransactions = (kioskTxns) => {
    return kioskTxns.map(txn => {
      const bookingInfos = Array.isArray(txn.booking_infos) ? txn.booking_infos : [];
      const firstBooking = bookingInfos[0] || {};
      const courts = [...new Set(bookingInfos.map(b => `Court ${b.court_no || b.court_id}`))].join(', ');
      // Compute time range across all bookings
      let startTime = '', endTime = '';
      if (bookingInfos.length === 1) {
        const b = bookingInfos[0];
        if (b.booking_time_start) startTime = b.booking_time_start;
        if (b.booking_time_end) endTime = b.booking_time_end;
        if (!startTime && b.time_slots?.length) {
          startTime = b.time_slots[0];
          endTime = b.time_slots[b.time_slots.length - 1];
        }
      }
      return {
        _type: 'kiosk_transaction',
        booking_id: txn.transaction_code || txn._id,
        customer_name: txn.name || 'Kiosk Guest',
        customer_phone: txn.contact_number || '',
        customer_email: txn.email || '',
        payment_status: txn.payment_status === 'paid' ? 'paid' : (txn.payment_status || 'pending'),
        payment_method: txn.payment_type === 'online' ? 'Card' : txn.payment_type === 'counter' ? 'Counter' : (txn.payment_type || '-'),
        transaction_ref: txn.transaction_code,
        total_amount: txn.total_amount || 0,
        date: firstBooking.booking_date || '',
        start_time: startTime,
        end_time: endTime,
        court_name: courts,
        created_at: txn.created_at || txn.updated_at || '',
        booked_at: txn.created_at || txn.updated_at || '',
        kiosk_booking_infos: bookingInfos,
      };
    });
  };

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let url = `${API}/admin/bookings?include_cancelled=true&limit=500`;
      if (dateFrom) url += `&start_date=${dateFrom}`;
      if (dateTo) url += `&end_date=${dateTo}`;

      // Fetch bookings and POS orders in parallel
      let posUrl = `${API}/admin/pos/orders?limit=500`;
      if (dateFrom) posUrl += `&date=${dateFrom}`;

      const [bookingRes, posRes, kioskTxns] = await Promise.all([
        fetch(url, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(posUrl, { headers: { Authorization: `Bearer ${token}` } }).catch(() => null),
        fetchKioskTransactions()
      ]);

      let combinedList = [];

      if (bookingRes.ok) {
        const data = await bookingRes.json();
        const list = Array.isArray(data) ? data : (data.bookings || data.data || []);
        combinedList = [...list];
      } else {
        const errData = await bookingRes.json().catch(() => ({}));
        setError(errData.detail || 'Failed to fetch bookings');
      }

      // Merge POS orders — normalize them to look like booking entries
      if (posRes && posRes.ok) {
        const posData = await posRes.json();
        const posOrders = posData.orders || [];
        const normalizedPOS = posOrders.map(order => ({
          _type: 'pos_order',
          booking_id: order.order_id,
          customer_name: order.customer_name || 'Guest',
          payment_status: order.status === 'completed' ? 'paid' : (order.status || 'completed'),
          payment_method: order.payment_method,
          transaction_ref: order.transaction_ref,
          total_amount: order.total,
          created_at: order.created_at,
          booked_at: order.created_at,
          created_by: order.created_by,
          created_by_name: order.created_by_name,
          notes: order.notes,
          pos_items: order.items || [],
        }));
        combinedList = [...combinedList, ...normalizedPOS];
      }

      // Merge kiosk transactions
      if (kioskTxns && kioskTxns.length > 0) {
        const normalizedKiosk = normalizeKioskTransactions(kioskTxns);
        combinedList = [...combinedList, ...normalizedKiosk];
      }

      // Sort newest first
      combinedList.sort((a, b) => {
        const da = new Date(a.booked_at || a.created_at || a.date || 0);
        const db2 = new Date(b.booked_at || b.created_at || b.date || 0);
        return db2 - da;
      });
      setBookings(combinedList);
    } catch (e) {
      console.error('Failed to fetch bookings:', e);
      setError(e.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [token, dateFrom, dateTo]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  // Fetch add-ons master data for price lookups
  useEffect(() => {
    const fetchAddOns = async () => {
      try {
        const res = await fetch(`${API}/admin/add-ons`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAddOnsMaster(Array.isArray(data) ? data : (data.items || []));
        }
      } catch (e) {
        console.error('Failed to fetch add-ons master data:', e);
      }
    };
    if (token) fetchAddOns();
  }, [token]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, dateFrom, dateTo]);

  // WebSocket for realtime updates — auto-refresh when bookings change
  useEffect(() => {
    let ws = null;
    let reconnectTimer;
    let reconnectDelayMs = 2000;

    const connect = () => {
      try {
        const wsUrl = getWebSocketUrl();
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          reconnectDelayMs = 2000;
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'booking_created' || data.type === 'booking_updated' ||
                data.type === 'booking_deleted' || data.type === 'booking_cancelled' ||
                data.type === 'payment_confirmed') {
              // Silent re-fetch to update the log
              fetchBookings();
            }
          } catch (err) {
            // ignore parse errors
          }
        };

        ws.onerror = () => {};

        ws.onclose = () => {
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(() => {
            reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
            connect();
          }, reconnectDelayMs);
        };
      } catch (err) {
        // silently ignore
      }
    };

    connect();

    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (ws && ws.readyState === WebSocket.OPEN) ws.close();
    };
  }, [fetchBookings]);

  // Filter bookings
  const filteredBookings = bookings.filter((b) => {
    // Status filter
    if (statusFilter !== 'all') {
      const st = (b.payment_status || b.status || '').toLowerCase();
      if (statusFilter === 'paid' && st !== 'paid' && st !== 'confirmed') return false;
      if (statusFilter === 'unpaid' && st !== 'unpaid' && st !== 'pending' && st !== 'reserved') return false;
      if (statusFilter === 'cancelled' && st !== 'cancelled') return false;
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const bookingId = String(b.booking_id || '').toLowerCase();
      const customerName = String(b.customer_name || '').toLowerCase();
      const courtName = String(b.court_name || b.court_id || '').toLowerCase();
      const phone = String(b.customer_phone || '').toLowerCase();
      const email = String(b.customer_email || '').toLowerCase();
      const notes = String(b.notes || '').toLowerCase();
      // For POS orders, also search item names
      const posItemNames = (b.pos_items || []).map(i => String(i.name || '').toLowerCase()).join(' ');
      const txnRef = String(b.transaction_ref || '').toLowerCase();
      if (
        !bookingId.includes(q) &&
        !customerName.includes(q) &&
        !courtName.includes(q) &&
        !phone.includes(q) &&
        !email.includes(q) &&
        !notes.includes(q) &&
        !posItemNames.includes(q) &&
        !txnRef.includes(q)
      ) {
        return false;
      }
    }

    return true;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredBookings.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBookings = filteredBookings.slice(startIndex, endIndex);

  const viewReceipt = (booking) => {
    setSelectedBooking(booking);
    setReceiptModalOpen(true);
  };

  const printReceipt = () => {
    window.print();
  };

  // Get add-ons / equipment for a booking
  const getAddOns = (booking) => {
    const items = [];
    if (booking.add_ons && booking.add_ons.length > 0) {
      booking.add_ons.forEach(item => {
        // Look up from master data if price/name missing
        const masterItem = addOnsMaster.find(a => a.item_id === item.item_id);
        const price = item.price || item.unit_price || masterItem?.price || 0;
        const name = item.name || item.addon_name || masterItem?.name || 'Add-on';
        items.push({
          type: 'Add-on',
          name: name,
          quantity: item.quantity || 1,
          price: price,
          total: price * (item.quantity || 1)
        });
      });
    }
    if (booking.equipment_rentals && booking.equipment_rentals.length > 0) {
      booking.equipment_rentals.forEach(item => {
        const masterItem = addOnsMaster.find(a => a.item_id === item.item_id);
        const price = item.price || item.unit_price || masterItem?.price || 0;
        const name = item.name || item.equipment_name || masterItem?.name || 'Equipment';
        items.push({
          type: 'Equipment',
          name: name,
          quantity: item.quantity || 1,
          price: price,
          total: price * (item.quantity || 1)
        });
      });
    }
    return items;
  };

  // Compute total add-ons cost
  const getAddOnsCost = (booking) => {
    return getAddOns(booking).reduce((sum, item) => sum + item.total, 0);
  };

  const getPaymentStatusBadge = (booking) => {
    const st = (booking.payment_status || booking.status || '').toLowerCase();
    if (st === 'paid' || st === 'confirmed') {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
          <DollarSign className="h-3 w-3 mr-1" />
          Paid
        </Badge>
      );
    }
    if (st === 'unpaid' || st === 'pending' || st === 'reserved') {
      return (
        <Badge className="bg-amber-100 text-amber-700 border-amber-300">
          <Clock className="h-3 w-3 mr-1" />
          {st === 'reserved' ? 'Reserved' : 'Unpaid'}
        </Badge>
      );
    }
    if (st === 'cancelled') {
      return (
        <Badge className="bg-red-100 text-red-700 border-red-300">
          <X className="h-3 w-3 mr-1" />
          Cancelled
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className={isDarkMode ? 'text-slate-300' : 'text-gray-600'}>
        {st || 'Unknown'}
      </Badge>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <Card className={`${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-gray-200'}`}>
          <CardHeader>
            <div className="flex flex-col gap-4">
              {/* Title row */}
              <div className="flex items-center justify-between">
                <CardTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                  <Receipt className="h-5 w-5 text-amber-500" />
                  Web Booking Log
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchBookings}
                  className={`flex-shrink-0 ${isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-700/50' : 'border-gray-200 text-gray-800 hover:bg-gray-50'}`}
                  disabled={loading}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {/* Filters row */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Date Range */}
                <div className="flex items-center gap-2">
                  <Label className={`text-sm whitespace-nowrap ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>From:</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className={`w-36 h-8 text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300'}`}
                  />
                  <Label className={`text-sm whitespace-nowrap ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>To:</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className={`w-36 h-8 text-xs ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white' : 'bg-white border-gray-300'}`}
                  />
                </div>

                {/* Status Filter */}
                <div className={`flex items-center gap-1 p-1 rounded-lg ${isDarkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                  {['all', 'paid', 'unpaid', 'cancelled'].map((st) => (
                    <Button
                      key={st}
                      variant={statusFilter === st ? 'default' : 'ghost'}
                      size="sm"
                      onClick={() => setStatusFilter(st)}
                      className={`h-7 px-3 text-xs capitalize ${
                        statusFilter !== st
                          ? st === 'paid' ? 'text-emerald-600'
                            : st === 'unpaid' ? 'text-amber-600'
                            : st === 'cancelled' ? 'text-red-600'
                            : ''
                          : ''
                      }`}
                    >
                      {st}
                    </Button>
                  ))}
                </div>

                {/* Search */}
                <div className="relative flex-1 min-w-[180px]">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`} />
                  <Input
                    placeholder="Search name, ID, court, notes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={`pl-10 h-8 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-white placeholder:text-slate-500' : 'bg-white border-gray-300 placeholder:text-gray-400'}`}
                  />
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
                <p className={`mt-3 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>Loading receipts...</p>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className={`${isDarkMode ? 'text-rose-300' : 'text-rose-600'}`}>{error}</p>
                <Button variant="outline" size="sm" onClick={fetchBookings} className="mt-4">
                  Try Again
                </Button>
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className={`h-12 w-12 mx-auto mb-3 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} />
                <p className={`${isDarkMode ? 'text-slate-500' : 'text-gray-500'}`}>
                  {searchQuery ? 'No bookings match your search' : 'No booking receipts found'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Receipt Cards */}
                {paginatedBookings.map((booking, index) => {
                  const addOns = getAddOns(booking);
                  const addOnsCost = getAddOnsCost(booking);
                  // Use court_cost if stored, otherwise for legacy bookings total_amount IS the court cost
                  const courtCost = booking.court_cost != null
                    ? booking.court_cost
                    : (booking.total_amount || 0);
                  const displayTotal = booking.court_cost != null
                    ? (booking.total_amount || 0)
                    : ((booking.total_amount || 0) + addOnsCost);

                  return (
                    <div
                      key={booking.booking_id || index}
                      className={`rounded-lg border p-4 transition-colors cursor-pointer hover:shadow-md ${
                        isDarkMode
                          ? 'bg-slate-800/70 border-slate-700 hover:bg-slate-800'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => viewReceipt(booking)}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        {/* Left: Customer & Booking Info */}
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex flex-wrap items-center gap-2">
                            {getPaymentStatusBadge(booking)}
                            {(() => {
                              const srcInfo = getBookingSourceInfo(booking);
                              const SrcIcon = srcInfo.icon;
                              return (
                                <Badge className={`text-[10px] px-1.5 py-0 ${srcInfo.color}`}>
                                  <SrcIcon className="h-2.5 w-2.5 mr-0.5" />
                                  {srcInfo.label}
                                </Badge>
                              );
                            })()}
                            <span className={`font-mono text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                              #{(booking.booking_id || '').slice(-8)}
                            </span>
                            <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                              {formatDateTime12(booking.booked_at || booking.created_at)}
                            </span>
                            {/* Change indicators */}
                            {booking.rescheduled_at && (
                              <Badge className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 border-blue-300">
                                <ArrowRightLeft className="h-2.5 w-2.5 mr-0.5" />
                                Rescheduled
                              </Badge>
                            )}
                            {booking.addons_updated_at && (
                              <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 border-amber-300">
                                <Pencil className="h-2.5 w-2.5 mr-0.5" />
                                Modified
                              </Badge>
                            )}
                            {(booking.status || '').toLowerCase() === 'cancelled' && (
                              <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-700 border-red-300">
                                <Ban className="h-2.5 w-2.5 mr-0.5" />
                                Cancelled
                              </Badge>
                            )}
                          </div>

                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                            <span className={`font-medium flex items-center gap-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              <User className="h-3.5 w-3.5 text-slate-400" />
                              {booking.customer_name || 'Guest'}
                            </span>
                            {booking.customer_phone && (
                              <span className={`text-sm flex items-center gap-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                <Phone className="h-3 w-3" />
                                {booking.customer_phone}
                              </span>
                            )}
                          </div>

                          {/* Court booking info (not shown for POS orders) */}
                          {booking._type !== 'pos_order' && (
                            <div className={`flex flex-wrap items-center gap-x-4 gap-y-1 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3.5 w-3.5 text-amber-500" />
                                {formatDate(booking.date)} 
                              </span>
                              {booking.start_time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3.5 w-3.5 text-amber-500" />
                                  {booking._type === 'kiosk_transaction' ? formatKioskTime(booking.start_time) : formatTime12(booking.start_time)}
                                  {booking.end_time && ` - ${booking._type === 'kiosk_transaction' ? formatKioskTime(booking.end_time) : formatTime12(booking.end_time)}`}
                                </span>
                              )}
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-700'
                              }`}>
                                {booking.court_name || booking.court_id || 'Court'}
                              </span>
                            </div>
                          )}

                          {/* Kiosk multi-booking info */}
                          {booking._type === 'kiosk_transaction' && booking.kiosk_booking_infos && booking.kiosk_booking_infos.length > 1 && (
                            <div className={`flex flex-wrap items-center gap-1.5 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                              <Tablet className="h-3 w-3 text-violet-500" />
                              <span className={`text-xs font-medium ${isDarkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                                {booking.kiosk_booking_infos.length} bookings:
                              </span>
                              {booking.kiosk_booking_infos.map((bi, i) => (
                                <span
                                  key={i}
                                  className={`px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-violet-900/30 border border-violet-700' : 'bg-violet-50 border border-violet-200'}`}
                                >
                                  Court {bi.court_no || bi.court_id} • {bi.booking_date}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* POS items purchased */}
                          {booking._type === 'pos_order' && booking.pos_items && booking.pos_items.length > 0 && (
                            <div className={`flex flex-wrap items-center gap-1.5 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                              <ShoppingCart className="h-3 w-3 text-orange-500" />
                              <span className={`text-xs font-medium ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>Items Purchased:</span>
                              {booking.pos_items.map((item, i) => (
                                <span
                                  key={i}
                                  className={`px-1.5 py-0.5 rounded ${
                                    isDarkMode ? 'bg-orange-900/30 border border-orange-700' : 'bg-orange-50 border border-orange-200'
                                  }`}
                                >
                                  {item.quantity || 1}x {item.name}
                                </span>
                              ))}
                            </div>
                          )}

                          {/* Add-ons summary inline (for court bookings) */}
                          {booking._type !== 'pos_order' && addOns.length > 0 && (
                            <div className={`flex flex-wrap items-center gap-1.5 text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                              <ShoppingBag className="h-3 w-3" />
                              {addOns.map((item, i) => (
                                <span
                                  key={i}
                                  className={`px-1.5 py-0.5 rounded ${
                                    isDarkMode ? 'bg-slate-700/50' : 'bg-gray-100'
                                  }`}
                                >
                                  {item.quantity}x {item.name}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Right: Total & Action */}
                        <div className="flex sm:flex-col items-center sm:items-end gap-2 sm:gap-1">
                          <span className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {formatCurrency(displayTotal)}
                          </span>
                          <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                            {booking.payment_method || booking.transaction_type || '-'}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className={`h-7 px-2 text-xs ${isDarkMode ? 'text-amber-400 hover:bg-slate-700' : 'text-amber-600 hover:bg-amber-50'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              viewReceipt(booking);
                            }}
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            View Receipt
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200">
                    <div className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>
                      Showing {startIndex + 1}-{Math.min(endIndex, filteredBookings.length)} of {filteredBookings.length} receipts
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                        className={`h-8 px-3 ${isDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>

                      <div className="flex items-center gap-1">
                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                          let pageNum;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <Button
                              key={pageNum}
                              variant={currentPage === pageNum ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCurrentPage(pageNum)}
                              className={`h-8 w-8 p-0 ${
                                currentPage === pageNum
                                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                                  : isDarkMode
                                    ? 'border-slate-700 text-slate-300 hover:bg-slate-700'
                                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              {pageNum}
                            </Button>
                          );
                        })}
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                        className={`h-8 px-3 ${isDarkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* ═══════════ Receipt Detail Modal ═══════════ */}
        <Dialog open={receiptModalOpen} onOpenChange={setReceiptModalOpen}>
          <DialogContent className={`max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
            <DialogHeader>
              <DialogTitle className={`${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center gap-2`}>
                <Receipt className="h-5 w-5 text-amber-500" />
                {selectedBooking?._type === 'pos_order' ? 'POS Purchase Receipt' : selectedBooking?._type === 'kiosk_transaction' ? 'Kiosk Booking Receipt' : 'Booking Receipt'}
              </DialogTitle>
            </DialogHeader>

            {selectedBooking && (() => {
              const booking = selectedBooking;
              const addOns = getAddOns(booking);
              const addOnsCost = getAddOnsCost(booking);
              // Use court_cost if stored (newer bookings). For legacy bookings where
              // `court_cost` is missing but `total_amount` may already include add-ons,
              // derive the court cost by subtracting add-ons from total_amount.
              const courtCost = booking.court_cost != null
                ? booking.court_cost
                : Math.max(0, (Number(booking.total_amount) || 0) - addOnsCost);
              // Prefer explicit stored total_amount when available; otherwise compute it.
              const displayTotal = (Number(booking.total_amount) || 0) || (courtCost + addOnsCost);

              return (
                <div id="receipt-print-area" className="space-y-4 print:text-black">
                  {/* Status & Source Banner */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      {getPaymentStatusBadge(booking)}
                      {(() => {
                        const srcInfo = getBookingSourceInfo(booking);
                        const SrcIcon = srcInfo.icon;
                        return (
                          <Badge className={`${srcInfo.color}`}>
                            <SrcIcon className="h-3 w-3 mr-1" />
                            {srcInfo.label}
                          </Badge>
                        );
                      })()}
                    </div>
                    <span className={`text-xs font-mono ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                      #{booking.booking_id || '-'}
                    </span>
                  </div>

                  {/* Customer Info */}
                  <div className={`rounded-lg p-3 space-y-2 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      Customer Information
                    </h4>
                    <div className="space-y-1.5">
                      <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        <User className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        <span className="font-medium">{booking.customer_name || 'Guest'}</span>
                      </div>
                      {booking.customer_phone && (
                        <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                          <Phone className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                          {booking.customer_phone}
                        </div>
                      )}
                      {booking.customer_email && (
                        <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                          <Mail className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                          {booking.customer_email}
                        </div>
                      )}
                    </div>
                  </div>

{/* Booking Schedule (for court bookings and kiosk) */}
                  {booking._type !== 'pos_order' && booking._type !== 'kiosk_transaction' && (
                  <div className={`rounded-lg p-3 space-y-2 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      Booking Details
                    </h4>
                    <div className="space-y-1.5">
                      <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        <FileText className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        <span className="font-medium">{booking.court_name || booking.court_id || 'Court'}</span>
                      </div>
                      <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <Calendar className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        {formatDate(booking.date)}
                      </div>
                      <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <Clock className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        {formatTime12(booking.start_time)} - {formatTime12(booking.end_time)}
                        {booking.duration_hours && (
                          <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                            ({booking.duration_hours} hr{booking.duration_hours > 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                      {booking.num_players && (
                        <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                          <User className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                          {booking.num_players} player{booking.num_players > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                    </div>
                  )}

                  {/* Kiosk Booking Details */}
                  {booking._type === 'kiosk_transaction' && booking.kiosk_booking_infos && (
                    <div className={`rounded-lg p-3 space-y-2 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        Kiosk Booking Details
                      </h4>
                      <div className="space-y-2">
                        {booking.kiosk_booking_infos.map((bi, idx) => {
                          const timeDisplay = bi.booking_time_start && bi.booking_time_end
                            ? `${formatKioskTime(bi.booking_time_start)} - ${formatKioskTime(bi.booking_time_end)}`
                            : (bi.time_slots || []).map(t => formatKioskTime(t)).join(', ');
                          return (
                            <div key={idx} className={`p-2 rounded border ${isDarkMode ? 'bg-slate-700/30 border-slate-600' : 'bg-white border-gray-200'}`}>
                              <div className="flex items-center justify-between">
                                <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                  Court {bi.court_no || bi.court_id}
                                </span>
                                <span className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                  {bi.booking_date}
                                </span>
                              </div>
                              {timeDisplay && (
                                <div className={`flex items-center gap-1 mt-1 text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                  <Clock className="h-3 w-3 text-violet-400" />
                                  {timeDisplay}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                  </div>
                  )}

                  {/* Time Slots (if available) */}
                  {booking.time_slots && booking.time_slots.length > 0 && (
                    <div className={`rounded-lg p-3 space-y-2 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        Time Slots Booked
                      </h4>
                      <div className="flex flex-wrap gap-1.5">
                        {booking.time_slots.map((slot, i) => (
                          <span
                            key={i}
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              isDarkMode ? 'bg-amber-900/30 text-amber-400 border border-amber-700' : 'bg-amber-50 text-amber-700 border border-amber-200'
                            }`}
                          >
                            {formatTime12(slot)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Items Breakdown */}
                  <div className={`rounded-lg p-3 space-y-2 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      {booking._type === 'pos_order' ? 'Items Purchased' : booking._type === 'kiosk_transaction' ? 'Charges' : 'Items & Charges'}
                    </h4>
                    <div className="space-y-1.5">
                      {/* Kiosk booking charge */}
                      {booking._type === 'kiosk_transaction' ? (
                        <>
                          <div className={`flex items-center justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                            <span className="flex items-center gap-1.5">
                              <Tablet className="h-3.5 w-3.5 text-violet-400" />
                              Kiosk Court Rental ({booking.kiosk_booking_infos?.length || 1} booking{(booking.kiosk_booking_infos?.length || 1) > 1 ? 's' : ''})
                            </span>
                            <span className="font-medium">{formatCurrency(booking.total_amount)}</span>
                          </div>
                        </>
                      ) : booking._type === 'pos_order' ? (
                        <>
                          {(booking.pos_items || []).map((item, i) => (
                            <div key={i} className={`flex items-center justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                              <span className="flex items-center gap-1.5">
                                <ShoppingCart className="h-3.5 w-3.5 text-orange-400" />
                                {item.quantity || 1}x {item.name}
                                {item.category && (
                                  <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                    ({item.category})
                                  </span>
                                )}
                              </span>
                              <span className="font-medium">{formatCurrency((item.price || 0) * (item.quantity || 1))}</span>
                            </div>
                          ))}
                        </>
                      ) : (
                        <>
                          {/* Court charge */}
                          <div className={`flex items-center justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                            <span className="flex items-center gap-1.5">
                              <CreditCard className="h-3.5 w-3.5 text-slate-400" />
                              Court Rental ({booking.duration_hours || 1} hr{(booking.duration_hours || 1) > 1 ? 's' : ''})
                            </span>
                            <span className="font-medium">{formatCurrency(courtCost)}</span>
                          </div>

                          {/* Add-ons / Equipment */}
                          {addOns.length > 0 ? (
                            <>
                              <div className={`border-t my-2 ${isDarkMode ? 'border-slate-600' : 'border-gray-200'}`} />
                              {addOns.map((item, i) => (
                                <div key={i} className={`flex items-center justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>
                                  <span className="flex items-center gap-1.5">
                                    <Package className="h-3.5 w-3.5 text-slate-400" />
                                    {item.quantity}x {item.name}
                                    <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                      ({item.type})
                                    </span>
                                  </span>
                                  <span className="font-medium">{formatCurrency(item.total)}</span>
                                </div>
                              ))}
                            </>
                          ) : (
                            <>
                              <div className={`border-t my-2 ${isDarkMode ? 'border-slate-600' : 'border-gray-200'}`} />
                              <div className={`text-sm italic ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                No add-ons or equipment rented
                              </div>
                            </>
                          )}
                        </>
                      )}

                      {/* Total */}
                      <div className={`border-t pt-2 mt-2 flex items-center justify-between ${isDarkMode ? 'border-slate-600' : 'border-gray-200'}`}>
                        <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          Total Amount
                        </span>
                        <span className={`text-lg font-bold ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                          {formatCurrency(displayTotal)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Info */}
                  <div className={`rounded-lg p-3 space-y-2 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                    <h4 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                      Payment Information
                    </h4>
                    <div className="space-y-1.5">
                      <div className={`flex items-center justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <span>Method</span>
                        <span className={`font-medium capitalize ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {(booking.payment_method || booking.transaction_type || '-').replace(/_/g, ' ')}
                        </span>
                      </div>
                      {booking.payment_transaction_id && (
                        <div className={`flex items-center justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                          <span>Transaction ID</span>
                          <span className={`font-mono text-xs ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {booking.payment_transaction_id}
                          </span>
                        </div>
                      )}
                      {booking.transaction_ref && (
                        <div className={`flex items-center justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                          <span>Reference #</span>
                          <span className={`font-mono text-xs ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {booking.transaction_ref}
                          </span>
                        </div>
                      )}
                      <div className={`flex items-center justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <span>{booking._type === 'pos_order' ? 'Purchased At' : booking._type === 'kiosk_transaction' ? 'Booked At (Kiosk)' : 'Booked At'}</span>
                        <span className={`${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {formatDateTime12(booking.booked_at || booking.created_at)}
                        </span>
                      </div>
                      <div className={`flex items-center justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                        <span>Source</span>
                        {(() => {
                          const srcInfo = getBookingSourceInfo(booking);
                          const SrcIcon = srcInfo.icon;
                          return (
                            <Badge className={`${srcInfo.color}`}>
                              <SrcIcon className="h-3 w-3 mr-1" />
                              {srcInfo.label}
                            </Badge>
                          );
                        })()}
                      </div>
                      {/* Created by admin */}
                      {booking.created_by_admin_name && (
                        <div className={`flex items-center justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                          <span className="flex items-center gap-1">
                            <Shield className="h-3 w-3" />
                            Created By
                          </span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {booking.created_by_admin_name}
                          </span>
                        </div>
                      )}
                      {/* Payment updated by admin */}
                      {booking.payment_updated_by_name && (
                        <div className={`flex items-center justify-between text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                          <span className="flex items-center gap-1">
                            <CheckCircle className="h-3 w-3" />
                            Payment Confirmed By
                          </span>
                          <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {booking.payment_updated_by_name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Notes */}
                  {booking.notes && (
                    <div className={`rounded-lg p-3 space-y-1 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        Notes
                      </h4>
                      <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-gray-700'}`}>{booking.notes}</p>
                    </div>
                  )}

                  {/* Change History / Activity Log */}
                  {(booking.rescheduled_at || booking.addons_updated_at || booking.notes_updated_at || booking.cancelled_at || booking.payment_updated_at) && (
                    <div className={`rounded-lg p-3 space-y-2 ${isDarkMode ? 'bg-slate-700/50' : 'bg-gray-50'}`}>
                      <h4 className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                        <History className="h-3.5 w-3.5" />
                        Change History
                      </h4>
                      <div className="space-y-2">
                        {/* Rescheduled */}
                        {booking.rescheduled_at && (
                          <div className={`p-2 rounded border ${isDarkMode ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                            <div className="flex items-center gap-1.5 mb-1">
                              <ArrowRightLeft className={`h-3.5 w-3.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                              <span className={`text-xs font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-700'}`}>Rescheduled</span>
                              {booking.rescheduled_by_name && (
                                <span className={`text-xs ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>by {booking.rescheduled_by_name}</span>
                              )}
                              <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                {formatDateTime12(booking.rescheduled_at)}
                              </span>
                            </div>
                            {(booking.previous_date || booking.previous_time || booking.previous_court) && (
                              <div className={`text-xs space-y-0.5 ${isDarkMode ? 'text-slate-300' : 'text-gray-600'}`}>
                                {booking.previous_date && (
                                  <div>Date: <span className="line-through opacity-60">{formatDate(booking.previous_date)}</span> → <span className="font-medium">{formatDate(booking.date)}</span></div>
                                )}
                                {booking.previous_time && (
                                  <div>Time: <span className="line-through opacity-60">{formatTime12(booking.previous_time)}</span> → <span className="font-medium">{formatTime12(booking.start_time)}</span></div>
                                )}
                                {booking.previous_court && booking.previous_court !== booking.court_id && (
                                  <div>Court: <span className="line-through opacity-60">{booking.previous_court}</span> → <span className="font-medium">{booking.court_id}</span></div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Add-ons Updated */}
                        {booking.addons_updated_at && (
                          <div className={`p-2 rounded border ${isDarkMode ? 'bg-amber-900/20 border-amber-800' : 'bg-amber-50 border-amber-200'}`}>
                            <div className="flex items-center gap-1.5">
                              <Pencil className={`h-3.5 w-3.5 ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`} />
                              <span className={`text-xs font-semibold ${isDarkMode ? 'text-amber-400' : 'text-amber-700'}`}>Add-ons Updated</span>
                              {booking.addons_updated_by_name && (
                                <span className={`text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>by {booking.addons_updated_by_name}</span>
                              )}
                              <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                {formatDateTime12(booking.addons_updated_at)}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Notes Updated */}
                        {booking.notes_updated_at && (
                          <div className={`p-2 rounded border ${isDarkMode ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}>
                            <div className="flex items-center gap-1.5">
                              <FileText className={`h-3.5 w-3.5 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`} />
                              <span className={`text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-gray-600'}`}>Notes Updated</span>
                              {booking.notes_updated_by_name && (
                                <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-gray-500'}`}>by {booking.notes_updated_by_name}</span>
                              )}
                              <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                {formatDateTime12(booking.notes_updated_at)}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Payment Confirmed */}
                        {booking.payment_updated_at && (
                          <div className={`p-2 rounded border ${isDarkMode ? 'bg-emerald-900/20 border-emerald-800' : 'bg-emerald-50 border-emerald-200'}`}>
                            <div className="flex items-center gap-1.5">
                              <CheckCircle className={`h-3.5 w-3.5 ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} />
                              <span className={`text-xs font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>Payment Confirmed</span>
                              {booking.payment_updated_by_name && (
                                <span className={`text-xs ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>by {booking.payment_updated_by_name}</span>
                              )}
                              <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                {formatDateTime12(booking.payment_updated_at)}
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Cancelled */}
                        {booking.cancelled_at && (
                          <div className={`p-2 rounded border ${isDarkMode ? 'bg-red-900/20 border-red-800' : 'bg-red-50 border-red-200'}`}>
                            <div className="flex items-center gap-1.5">
                              <X className={`h-3.5 w-3.5 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
                              <span className={`text-xs font-semibold ${isDarkMode ? 'text-red-400' : 'text-red-700'}`}>Cancelled</span>
                              {booking.cancelled_by_name && (
                                <span className={`text-xs ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>by {booking.cancelled_by_name}</span>
                              )}
                              <span className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                                {formatDateTime12(booking.cancelled_at)}
                              </span>
                            </div>
                            {booking.cancellation_reason && (
                              <p className={`text-xs mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
                                Reason: {booking.cancellation_reason}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Print Button */}
                  {/* <div className="flex justify-end pt-2 print:hidden">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={printReceipt}
                      className={`${isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
                    >
                      <Printer className="h-4 w-4 mr-2" />
                      Print Receipt
                    </Button>
                  </div> */}
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #receipt-print-area, #receipt-print-area * { visibility: visible; }
          #receipt-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 80mm;
            padding: 8px;
            font-size: 11px;
          }
        }
      `}</style>
    </AdminLayout>
  );
};

export default AdminWebLog;
